import React from 'react';
import clsx from 'clsx';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

import { Button, Text } from '../../components';
import { Input } from '../../components/Input';
import { USER_TURN_TEMPLATE_VARIABLE } from '../AgentForm/const';
import { getUserConfidenceRangeFieldErrors } from '../AgentForm/utils/validateUserConfidenceRanges';
import type { UserConfidenceRange } from '../../utilities';

export type UserConfidenceRangesListProps = {
  value?: UserConfidenceRange[] | null;
  onChange?: (value: UserConfidenceRange[]) => void;
  disabled?: boolean;
  error?: string;
};

const EMPTY_RANGE: UserConfidenceRange = { min: 0, max: 0, template: '' };

const TEMPLATE_INPUT_CLASSES =
  'py-[0.7rem] px-6 text-mainText text-medium w-full appearance-none';

const renderTemplateHighlight = (template: string): React.ReactNode => {
  if (!template.includes(USER_TURN_TEMPLATE_VARIABLE)) {
    return template;
  }

  const parts = template.split(USER_TURN_TEMPLATE_VARIABLE);

  return parts.map((part, idx) => (
    <React.Fragment key={idx}>
      {part}
      {idx < parts.length - 1 && (
        <mark className="bg-sky-200 text-sky-950 rounded-sm">
          {USER_TURN_TEMPLATE_VARIABLE}
        </mark>
      )}
    </React.Fragment>
  ));
};

type TemplateInputProps = {
  value: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  defaultSizeClass?: string;
};

const TemplateInput: React.FC<TemplateInputProps> = ({
  value,
  onChange,
  disabled,
  placeholder,
  defaultSizeClass = 'w-full',
}) => (
  <div
    className={clsx(
      'relative h-fit rounded-bt shadow-sm border border-solid border-border bg-contrast',
      defaultSizeClass,
      disabled && 'opacity-50 pointer-events-none'
    )}
  >
    <div
      aria-hidden
      className={clsx(
        'pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words',
        TEMPLATE_INPUT_CLASSES
      )}
    >
      {renderTemplateHighlight(value)}
    </div>
    <input
      type="text"
      value={value}
      disabled={disabled}
      placeholder={placeholder}
      spellCheck={false}
      onChange={e => onChange?.(e.target.value)}
      className={clsx(
        'relative bg-transparent text-transparent caret-mainText placeholder:text-gray-400 outline-none rounded-bt',
        TEMPLATE_INPUT_CLASSES
      )}
    />
  </div>
);

const TemplateFieldFooter: React.FC<{ template: string }> = ({ template }) => {
  if (template.includes(USER_TURN_TEMPLATE_VARIABLE)) return null;

  return (
    <Text
      as="span"
      variant="secondary"
      className="text-small italic text-yellow-600"
    >
      Warning: template does not contain {USER_TURN_TEMPLATE_VARIABLE}
    </Text>
  );
};

export const UserConfidenceRangesList: React.FC<
  UserConfidenceRangesListProps
> = ({ value, onChange, disabled, error }) => {
  const ranges = value ?? null;

  const addItem = () => onChange?.([...(ranges || []), { ...EMPTY_RANGE }]);

  const removeItem = (index: number) => {
    const updated = (ranges || []).filter((_, i) => i !== index);
    onChange?.(updated);
  };

  const updateField = (
    index: number,
    field: keyof UserConfidenceRange,
    newValue: string | number
  ) => {
    const updated = [...(ranges || [])];
    updated[index] = { ...updated[index], [field]: newValue };
    onChange?.(updated);
  };

  const updateBound = (
    index: number,
    field: 'min' | 'max',
    newValue: string
  ) => {
    updateField(index, field, newValue);
  };

  return (
    <div className="w-full space-y-3 py-1">
      {(ranges || []).map((item, i) => {
        const fieldErrors = getUserConfidenceRangeFieldErrors(item);
        const hasFieldError =
          !!fieldErrors.min || !!fieldErrors.max || !!fieldErrors.template;

        return (
          <div
            key={i}
            className={`flex items-start gap-3 p-3 rounded-xl transition-colors ${
              hasFieldError
                ? 'border border-red-500 bg-red-50'
                : 'border border-gray-200 bg-gray-50'
            }`}
          >
            <div className="flex flex-col gap-3 flex-1">
              <div className="flex items-start gap-4 flex-wrap">
                <div className="flex flex-col gap-1">
                  <Text
                    as="span"
                    variant="secondary"
                    className="text-small shrink-0"
                  >
                    Min
                  </Text>
                  <Input
                    numeric
                    roundTo={4}
                    min={0}
                    max={1}
                    disabled={disabled}
                    value={item.min}
                    onChange={val => updateBound(i, 'min', val)}
                    defaultSizeClass="w-24"
                  />
                  {!!fieldErrors.min && (
                    <Text variant="error" className="text-small italic">
                      {fieldErrors.min}
                    </Text>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <Text
                    as="span"
                    variant="secondary"
                    className="text-small shrink-0"
                  >
                    Max
                  </Text>
                  <Input
                    numeric
                    roundTo={4}
                    min={0}
                    max={1}
                    disabled={disabled}
                    value={item.max}
                    onChange={val => updateBound(i, 'max', val)}
                    defaultSizeClass="w-24"
                  />
                  {!!fieldErrors.max && (
                    <Text variant="error" className="text-small italic">
                      {fieldErrors.max}
                    </Text>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-1 w-full">
                <Text
                  as="span"
                  variant="secondary"
                  className="text-small shrink-0"
                >
                  Template
                </Text>
                <TemplateInput
                  disabled={disabled}
                  value={item.template}
                  onChange={val => updateField(i, 'template', val)}
                  placeholder={`e.g. LOW CONFIDENCE <${USER_TURN_TEMPLATE_VARIABLE}>`}
                  defaultSizeClass="w-full"
                />
                <TemplateFieldFooter template={item.template} />
              </div>
            </div>

            {!disabled && (
              <Button
                type="empty"
                size="small"
                tooltip="Remove range"
                icon={<DeleteIcon fontSize="medium" />}
                onClick={() => removeItem(i)}
                className="text-danger hover:opacity-70 mt-1 shrink-0"
              />
            )}
          </div>
        );
      })}

      {!!error && (
        <Text variant="error" className="text-small italic">
          {error}
        </Text>
      )}

      {!disabled && (
        <Button
          size="small"
          type="outline"
          icon={<AddIcon fontSize="small" />}
          disabled={disabled}
          label="Add Range"
          onClick={addItem}
        />
      )}
    </div>
  );
};
